import { Layout } from 'antd';
import React, { useContext, useEffect } from 'react';
import Helmet from 'react-helmet';
import { connect } from 'react-redux';
import { Redirect, Route, Switch, withRouter } from 'react-router-dom';
import type { Dispatch } from 'redux';

import { SideMenuAdmin } from 'app/components/layout/SideMenuAdmin';
import { Bills } from 'app/components/pages/Bills/Bills';
import { CollectionDetail } from 'app/components/pages/Collections/CollectionDetail/CollectionDetail';
import { Collections } from 'app/components/pages/Collections/Collections';
import { CommercialGestures } from 'app/components/pages/CommercialGestures/CommercialGestures';
import { CustomTags } from 'app/components/pages/CustomTags/CustomTags';
import { EditoEventDetail } from 'app/components/pages/Edito/Event/Detail/EditoEventDetail';
import { EditoEventListPage } from 'app/components/pages/Edito/Event/List/EditoEventListPage';
import { Galleries } from 'app/components/pages/Galleries/Galleries';
import { GalleryDetail } from 'app/components/pages/Galleries/GalleryDetail/GalleryDetail';
import { GiftCardsList } from 'app/components/pages/GiftCards';
import { GiftCardDetailsPage } from 'app/components/pages/GiftCards/details';
import { GuidePage } from 'app/components/pages/Guides/Guide/Guide';
import { Home } from 'app/components/pages/Home';
import { HotelsList } from 'app/components/pages/Hotels';
import { HotelEdit } from 'app/components/pages/Hotels/Form/Edit';
import { NewHotel } from 'app/components/pages/Hotels/NewHotel/NewHotel';
import { PackageDetail } from 'app/components/pages/Packs/PackageDetail/PackageDetail';
import { QuickFilterDetail } from 'app/components/pages/QuickFilters/QuickFilterDetail/QuickFilterDetail';
import { QuickFiltersListPage } from 'app/components/pages/QuickFilters/QuickFilterListPage/QuickFiltersListPage';
import { RoomDetail } from 'app/components/pages/Rooms/RoomDetail/RoomDetail';
import { Rooms } from 'app/components/pages/Rooms/RoomsRange/Rooms';
import { SalesList } from 'app/components/pages/Sales';
import { Slider } from 'app/components/pages/Slider';
import { UsersList } from 'app/components/pages/Users';
import { UserDetails } from 'app/components/pages/Users/details';
import { Vouchers } from 'app/components/pages/Vouchers/Vouchers';
import { ConfigContext } from 'app/context/ConfigContext/ConfigContext';
import { UserContext } from 'app/context/UserContext/UserContext';
import { initApp } from 'app/redux/actions/navigation';

import { BookingDetails } from './pages/Bookings/BookingDetails/BookingDetails';
import { BookingDetails as LegacyBookingDetails } from './pages/Bookings/details';
import { BookingsList } from './pages/Bookings/list';
import { ExperienceCreation } from './pages/Experiences/ExperienceCreation/ExperienceCreation';
import { ExperienceDetails } from './pages/Experiences/ExperienceDetails/ExperienceDetails';
import { Experiences } from './pages/Experiences/Experiences';
import { ModernExperienceDetails } from './pages/Experiences/Modern/ModernExperienceDetails';
import { GuidesListPage } from './pages/Guides/Guides';
import { PacksList } from './pages/Packs';
import { ModernPackDetails } from './pages/Packs/Common/Modern/ModernPackDetails';
import { PackCreation } from './pages/Packs/PackCreation/PackCreation';
import { ModernRoomDetail } from './pages/Rooms/Modern/ModernRoomDetail';
import { RoomCreation } from './pages/Rooms/RoomCreation/RoomCreation';
import { SEOLandingsDetail } from './pages/SEOLandings/SEOLandingDetail/SEOLandingDetail';
import { SEOLandings } from './pages/SEOLandings/SEOLandings';
import { Deliveries } from './pages/WelcomeKit/Deliveries/Deliveries';
import { WelcomeKit } from './pages/WelcomeKit/WelcomeKit';

type AdminAppProps = {
  appReady: boolean;
  init: () => void;
};

const AdminAppComponent: React.FC<AdminAppProps> = ({ appReady, init }) => {
  const { user, isAdmin, isSuperAdmin } = useContext(UserContext);
  const config = useContext(ConfigContext);

  useEffect(() => {
    init();
  }, [init]);

  if (!user || !appReady) {
    return null;
  }

  if (user && !isAdmin && config) {
    return <Redirect to={config.appUrl} />;
  }

  return (
    <>
      <Helmet>
        <title>Staycation - Admin</title>
      </Helmet>
      <Layout className="app">
        <SideMenuAdmin user={user} />
        <Switch>
          <Route path="/" exact component={Home} />
          <Route path="/rooms" exact component={Rooms} />
          <Route path="/rooms/new" component={RoomCreation} />
          <Route path="/rooms/legacy/:id" component={RoomDetail} />
          <Route path="/rooms/:id" component={ModernRoomDetail} />
          <Route path="/rooms/legacy/new" component={RoomDetail} />
          <Route path="/activities/new" component={ExperienceCreation} />
          <Route path="/activities/legacy/new" component={ExperienceDetails} />
          <Route path="/activities/legacy/:id" component={ExperienceDetails} />
          <Route path="/activities/:id" component={ModernExperienceDetails} />
          <Route path="/activities" exact component={Experiences} />
          <Route path="/hotels" exact component={HotelsList} />
          <Route path="/hotels/new" component={NewHotel} />
          <Route path="/hotels/:id/edit" component={HotelEdit} />
          <Route path="/hotels/galleries" component={Galleries} />
          <Route path="/hotels/:id/gallery" component={GalleryDetail} />
          <Route
            path="/bookings/legacy/:bookingCode"
            component={LegacyBookingDetails}
          />
          <Route path="/bookings/:bookingCode" component={BookingDetails} />
          <Route path="/bookings" component={BookingsList} />
          <Route path="/gift-cards/:id" component={GiftCardDetailsPage} />
          <Route path="/gift-cards" component={GiftCardsList} />
          <Route path="/packs" exact component={PacksList} />
          <Route path="/packs/legacy/new" component={PackageDetail} />
          <Route path="/packs/legacy/:id" component={PackageDetail} />
          <Route path="/packs/:id" component={ModernPackDetails} />
          <Route path="/packs/new" component={PackCreation} />
          <Route path="/bills" component={Bills} />
          <Route path="/sales" component={SalesList} />
          <Route path="/slider" exact component={Slider} />
          <Route path="/custom-tags" component={CustomTags} />
          <Route path="/guides" exact component={GuidesListPage} />
          <Route path="/guides/new" component={GuidePage} />
          <Route path="/guides/:id/edit" component={GuidePage} />
          <Route path="/collections" exact component={Collections} />
          <Route path="/collections/new" component={CollectionDetail} />
          <Route path="/collections/:id/edit" component={CollectionDetail} />
          <Route path="/users/:id/edit" component={UserDetails} />
          <Route path="/users" component={UsersList} />
          <Route path="/vouchers/new" component={Vouchers} exact />
          <Route path="/edito-events" exact component={EditoEventListPage} />
          <Route path="/edito-events/new" component={EditoEventDetail} />
          <Route
            path="/edito-events/:id/edit"
            exact
            component={EditoEventDetail}
          />
          <Route
            path="/welcome-kit/orders/:id/deliveries"
            component={Deliveries}
          />
          <Route path="/welcome-kit" component={WelcomeKit} />
          <Redirect from="/vouchers/:id/edit" to="/vouchers/new" />
          <Route
            path="/seo-landings/:slug/edit"
            component={SEOLandingsDetail}
          />
          <Route path="/seo-landings" component={SEOLandings} />
          {isSuperAdmin && (
            <>
              <Route
                path="/quick-filters"
                exact
                component={QuickFiltersListPage}
              />
              <Route path="/quick-filters/new" component={QuickFilterDetail} />
              <Route
                path="/quick-filters/:id/edit"
                component={QuickFilterDetail}
              />
              <Route
                path="/commercial-gestures"
                component={CommercialGestures}
                exact
              />
            </>
          )}
        </Switch>
      </Layout>
    </>
  );
};

const mapStateToProps = (state: { navigation: { appReady: boolean } }) => ({
  appReady: state.navigation.appReady,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  init: () => dispatch(initApp('admin')),
});

export const AdminApp = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(AdminAppComponent)
);
